import { APP_TENANT, APP_URL, getEnvironment } from "../constants/app-url"
import { parseAppModules, parseApplicationURL, parseIframeURLs } from "./iframe"

const OLD_SSE_APPCODE = "SSE"
const NEW_SSE_APPCODE = "SSEPRODIFRAME"

/**
 * Resolves the tenant value and domain from APP_URL/APP_TENANT.
 */
export function getAppUrlDomain() {
  const domain = APP_URL?.split(".").slice(-1)?.[0]
  let appTenantVal: string = APP_TENANT.substring(0, APP_TENANT.length - 1)
  if (!APP_URL.endsWith(".com") && !APP_URL.endsWith(".io")) {
    appTenantVal = `${APP_TENANT?.substring(
      0,
      APP_TENANT?.length - 1
    )}-${domain}`
  }
  return { appTenantVal, domain }
}

/**
 * Processes the raw applications response from the API:
 * - Parses module URLs with tenant substitution
 * - Parses iframe URLs for embedded apps
 * - Parses iframe logout URLs
 * - Removes deprecated SSE app if the new one exists
 */
export function processApplications(
  apps: any[],
  appTenantVal: string,
  domain: string,
  storeData: any,
  isSSEEUURL: boolean
) {
  const unmodifiedTenantHost = APP_TENANT.substring(0, APP_TENANT.length - 1)
  const userInfoTenants = storeData?.userInfo?.ext?.tenantId || []

  for (const app of apps) {
    app.modules = parseAppModules(app.modules, appTenantVal, domain)
    app.sharedModules = parseAppModules(app.sharedModules, appTenantVal, domain)

    if (app.openInIframe && app.iframeURL) {
      const useSSEEUUrl = isSSEEUURL && app.applicationURL === "/sse"
      app.iframeURL = parseIframeURLs(
        app.iframeURL,
        appTenantVal,
        storeData,
        useSSEEUUrl
      )
    }

    if (app.openInIframe && app.iframeLogoutURL) {
      app.iframeLogoutURL = app.iframeLogoutURL
        .replace("{tenantHost}", unmodifiedTenantHost)
        .replace("OIDC_ACCESS_TOKEN", storeData.userToken)
        .replace("ENV", getEnvironment())
        .replace("TENANT", appTenantVal)
        .replace("USERNAME", storeData?.userInfo?.userName)
    }

    if (app.applicationURL) {
      app.applicationURL = parseApplicationURL(
        app.applicationURL,
        userInfoTenants,
        unmodifiedTenantHost
      )
    }

    app.menus = app.menus?.map((menu) => {
      if (menu.iframeURL) {
        menu.iframeURL = parseIframeURLs(
          menu.iframeURL,
          appTenantVal,
          storeData,
          isSSEEUURL
        )
      }
      return menu
    })
  }

  // HARDCODED - Remove when SSE app is deprecated
  const hasOldSSEAndNewSSe =
    apps.some((a) => a.applicationCode === OLD_SSE_APPCODE) &&
    apps.some((a) => a.applicationCode === NEW_SSE_APPCODE)
  if (hasOldSSEAndNewSSe) {
    return apps.filter((a) => a.applicationCode !== OLD_SSE_APPCODE)
  }
  return apps
}

/**
 * Extracts feature flags from the utility app's menu localization keys.
 */
export function extractFeatureFlags(apps: any[]): {
  showHomepage: boolean
  useNewIframe: boolean
  isSSEEUURL: boolean
  isAIEnabled: boolean
} {
  const utilityApp = apps.find((a) => a.applicationCode === "UTL")
  if (!utilityApp) {
    return {
      showHomepage: false,
      useNewIframe: false,
      isSSEEUURL: false,
      isAIEnabled: false
    }
  }
  const menus = utilityApp?.menus?.map((a) => a.localizationKey) || []
  return {
    showHomepage: menus.includes("Home"),
    useNewIframe: menus.includes("NewIframe"),
    isSSEEUURL: menus.includes("SSE"),
    isAIEnabled: menus.includes("AI")
  }
}
